import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet, Link } from 'react-router-dom';
import { Layout, Menu, Select, Avatar, Dropdown, Space, message } from 'antd';
import { 
  DashboardOutlined, 
  FileTextOutlined, 
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  SwapOutlined,
  BankOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { getMerchantToken, getMerchantApps, getCurrentAppId, setCurrentAppId, clearMerchantAuth } from '../../utils/merchantApi';

const { Header, Sider, Content } = Layout;

const MerchantLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [apps, setApps] = useState([]);
  const [currentAppId, setCurrentAppLocal] = useState('');
  const [currentAppName, setCurrentAppName] = useState('');
  const [initialized, setInitialized] = useState(true);

  useEffect(() => {
    const token = getMerchantToken();
    if (!token) {
      navigate('/merchant/login', { replace: true });
      return;
    }

    const appList = getMerchantApps();
    if (!appList || appList.length === 0) {
      navigate('/merchant/login', { replace: true });
      return;
    }

    setApps(appList);

    const savedAppId = getCurrentAppId();
    if (savedAppId) {
      const app = appList.find(a => a.app_id === savedAppId);
      if (app) {
        setCurrentAppLocal(savedAppId);
        setCurrentAppName(app.app_name);
        return;
      }
    }

    if (appList.length >= 1) {
      setCurrentAppLocal(appList[0].app_id);
      setCurrentAppName(appList[0].app_name);
      setCurrentAppId(appList[0].app_id);
    }
  }, [navigate]);

  const handleAppChange = (appId) => {
    const app = apps.find(a => a.app_id === appId);
    if (app) {
      setCurrentAppLocal(appId);
      setCurrentAppName(app.app_name);
      setCurrentAppId(appId);
      message.success(`已切换到 ${app.app_name}`);
    }
  };

  const handleLogout = () => {
    clearMerchantAuth();
    message.success('已退出登录');
    navigate('/merchant/login');
  };

  const menuItems = [
    {
      key: '/merchant',
      icon: <DashboardOutlined />,
      label: <Link to="/merchant">数据统计</Link>,
    },
    {
      key: '/merchant/account',
      icon: <WalletOutlined />,
      label: <Link to="/merchant/account">账户流水</Link>,
    },
    {
      key: '/merchant/orders',
      icon: <FileTextOutlined />,
      label: <Link to="/merchant/orders">收款订单</Link>,
    },
    {
      key: '/merchant/withdraw',
      icon: <BankOutlined />,
      label: <Link to="/merchant/withdraw">代付订单</Link>,
    },
    {
      key: '/merchant/settlements',
      icon: <SwapOutlined />,
      label: <Link to="/merchant/settlements">结算记录</Link>,
    },
    {
      key: '/merchant/settings',
      icon: <SettingOutlined />,
      label: <Link to="/merchant/settings">应用设置</Link>,
    },
  ];

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider 
        collapsible 
        collapsed={collapsed} 
        onCollapse={setCollapsed}
        theme="dark"
        width={220}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div style={{
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: collapsed ? '14px' : '18px',
          fontWeight: 'bold',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}>
          {collapsed ? '商户' : '商户管理后台'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
        />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 220, transition: 'margin-left 0.2s' }}>
        <Header style={{ 
          padding: '0 24px', 
          background: '#fff', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span style={{ color: '#666' }}>当前应用：</span>
            {apps.length > 0 && (
              <Select
                value={currentAppId}
                onChange={handleAppChange}
                style={{ width: 200 }}
                placeholder="选择应用"
              >
                {apps.map(app => (
                  <Select.Option key={app.app_id} value={app.app_id}>
                    {app.app_name} ({app.app_id})
                  </Select.Option>
                ))}
              </Select>
            )}
          </div>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} />
              <span>商户用户</span>
            </Space>
          </Dropdown>
        </Header>
        <Content style={{ margin: '24px 16px', padding: 0, overflow: 'initial' }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MerchantLayout;
